import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadSettings, saveSettings } from '../settingsActions';
import { UI_MESSAGES } from '../../../shared/constants';
import { extractContractError, formatErrorForDisplay } from  '@bafkal/ui-shared/utils';
import { FormField, TextInput, ToggleSwitch } from '@bafkal/ui-shared/forms';
import { Card, ErrorMessage, Button, LoadingSpinner } from '@bafkal/ui-shared/components';
import { colors } from '@bafkal/ui-shared/tokens';

import ApiKeysSection from './ApiKeysSection';

const DEFAULT_SETTINGS = {
  notificationEmail: '',
  timezone: '',
  defaultFloorPriceOverride: '',
  active: true,
};

const PublisherSettingsForm = () => {
  const dispatch = useDispatch();
  const { loading: reduxLoading, settings: reduxSettings } = useSelector(
    (state) => state.settings
  );

  const [formData, setFormData] = useState(DEFAULT_SETTINGS);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    dispatch(loadSettings()).catch(() => {});
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (reduxSettings) {
      setFormData({
        notificationEmail: reduxSettings.notificationEmail || '',
        timezone: reduxSettings.timezone || '',
        defaultFloorPriceOverride:
          reduxSettings.defaultFloorPriceOverride != null
            ? String(reduxSettings.defaultFloorPriceOverride)
            : '',
        active: reduxSettings.active !== undefined ? Boolean(reduxSettings.active) : true,
      });
    }
  }, [reduxSettings]);

  // Handles both standard inputs (TextInput) and the ToggleSwitch event interface.
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isMounted.current) {
      setSaveLoading(true);
      setError(null);
      setSuccess(null);
    }

    const payload = {
      notificationEmail: formData.notificationEmail || null,
      timezone: formData.timezone || null,
      defaultFloorPriceOverride:
        formData.defaultFloorPriceOverride !== ''
          ? parseFloat(formData.defaultFloorPriceOverride)
          : null,
      active: formData.active,
    };

    try {
      await dispatch(saveSettings(payload));
      if (isMounted.current) setSuccess(UI_MESSAGES.SETTINGS.SAVE_SUCCESS);
    } catch (err) {
      const contractError = extractContractError(err, UI_MESSAGES.SETTINGS.SAVE_FAILED);
      if (isMounted.current) setError(formatErrorForDisplay(contractError));
    } finally {
      if (isMounted.current) setSaveLoading(false);
    }
  };

  if (reduxLoading && !reduxSettings) {
    return (
      <div className="py-8 flex justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card title="Publisher Settings" subtitle="Manage your publisher account preferences and defaults.">
          <ErrorMessage message={error} />

          {success && (
            <div style={{ backgroundColor: colors.success[50], borderLeft: `4px solid ${colors.success[500]}`, padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
              <p style={{ margin: 0, color: colors.success[700], fontWeight: 500 }}>{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
            <FormField label="Notification Email">
              <TextInput
                type="email"
                id="notificationEmail"
                name="notificationEmail"
                value={formData.notificationEmail}
                onChange={handleChange}
                placeholder="you@example.com"
              />
            </FormField>

            <FormField label="Timezone" helperText="IANA timezone identifier (e.g. America/New_York)">
              <TextInput
                type="text"
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                placeholder="America/New_York"
              />
            </FormField>

            <FormField
              label="Default Floor Price Override (USD)"
              helperText="Overrides ad unit floor prices globally. Leave blank to use per-unit floor prices."
            >
              <TextInput
                type="number"
                id="defaultFloorPriceOverride"
                name="defaultFloorPriceOverride"
                min="0"
                step="0.01"
                value={formData.defaultFloorPriceOverride}
                onChange={handleChange}
                placeholder="0.00"
              />
            </FormField>

            <FormField helperText="Enable ad serving for this publisher account.">
              <ToggleSwitch
                name="active"
                checked={formData.active}
                onChange={handleChange}
                label="Account Active"
              />
            </FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px' }}>
              <Button type="submit" variant="primary" disabled={saveLoading}>
                {saveLoading ? 'Saving…' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </Card>

        <ApiKeysSection />
      </div>
    </div>
  );
};

export default PublisherSettingsForm;

